import { useSelector, useDispatch } from 'react-redux';
import { useState, useEffect, useMemo, useRef } from 'react';
import Tooltip from '@material-ui/core/Tooltip';

import { toastError, toastSuccess } from '@components/Toast';

import { ReactComponent as LoadIcon } from '@/assets/icons/Load.svg';
import { ReactComponent as MissingIcon } from '@/assets/icons/supplierInfo.svg';
import { ReactComponent as SuccessIcon } from '@/assets/icons/success-page.svg';
import LoaderComponent from '@/components/LoaderComponent';
import CommonImage from '@/components/CommonImage';
import { getShortName } from '@/utils/Util';

import {
	useGetWashListQuery,
	useGetPrintListQuery,
	useGetEmbroideryListQuery,
	useGetCategorySubCategoryListQuery,
	useGetMarketListQuery,
	useGetMetarialFabricListQuery,
	useMaterialSearchOrAddMutation,
	useSetProductStyleInfoMutation,
	useSetProductInfoMutation
} from '../api/MultipleTechpackJourneyApi';
import { setDocumentExtractionSocketMessage } from '../redux/multipleTechpackJourneySlice';
import {
	applyFabricDropdownToExtraction,
	applyFabricModalPayloadToExtraction,
	buildMaterialSearchOrAddBody,
	buildSelectedFabricInfoValue,
	compositionRowsFromText,
	EMPTY_BY_PRODUCT_ID,
	extractionText,
	findListMatch,
	formatCompositionFromBodyFabric,
	getFabricationRawFields,
	normalizeList,
	TP_ROW_EMPTY_LABEL
} from '../utils';

import AddFabricCompositionModal from './AddFabricCompositionModal';
import Preview from './Preview';
import UpdateTechpackInfoForm from './UpdateTechpackInfoForm';

const MultipleTechpackPreview = ({ productIds, extractionPendingList, MergedFilesCount }) => {
	const dispatch = useDispatch();
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [buyerDesignNoInput, setBuyerDesignNoInput] = useState('');
	const [marketSelection, setMarketSelection] = useState(null);
	const [categorySelection, setCategorySelection] = useState(null);
	const [printSelection, setPrintSelection] = useState(null);
	const [embroiderySelection, setEmbroiderySelection] = useState(null);
	const [washSelection, setWashSelection] = useState(null);
	const [metarialFabricSelection, setMetarialFabricSelection] = useState(null);
	const [selectedFabricInfoValue, setSelectedFabricInfoValue] = useState('');
	const [isShowUpdateMissingFabricInfoModal, setIsShowUpdateMissingFabricInfoModal] = useState(false);
	const [selectedDocUrl, setSelectedDocUrl] = useState('');
	const [previewDocName, setPreviewDocName] = useState('');
	/** Fabric from modal; applied to Redux only when user clicks main Update. Keyed by productId. */
	const [pendingFabricByProductId, setPendingFabricByProductId] = useState({});
	/** After Update, list row shows Mark as complete instead of Missing (per productId). */
	const [showMarkCompleteAfterUpdateByProductId, setShowMarkCompleteAfterUpdateByProductId] = useState({});
	/** Staged edits from Update button; applied only after Mark as complete click. */
	const [pendingUpdateByProductId, setPendingUpdateByProductId] = useState({});
	/** Hide rows from list after successful Mark as complete. */
	const [removedByProductId, setRemovedByProductId] = useState({});
	const [isMarkedAsAnimate, setIsMarkedAsAnimate] = useState(null);
	const markCompleteRemoveTimeRef = useRef(null);

	const [washList, setWashList] = useState([]);
	const [printList, setPrintList] = useState([]);
	const [embroideryList, setEmbroideryList] = useState([]);
	const [categorySubCategoryList, setCategorySubCategoryList] = useState([]);
	const [marketList, setMarketList] = useState([]);
	const [metarialFabricList, setMetarialFabricList] = useState([]);
	const [metarialFabricSearch, setMetarialFabricSearch] = useState('');

	const [isUpdateFormToggle, setIsUpdateFormToggle] = useState(true);

	const documentExtractionSocket = useSelector(
		(state) => state.multipleTechpackJourneyStore.documentExtractionSocket
	);
	const { data: washListData } = useGetWashListQuery();
	const { data: printListData } = useGetPrintListQuery();
	const { data: embroideryListData } = useGetEmbroideryListQuery();
	const { data: categorySubCategoryListData } = useGetCategorySubCategoryListQuery();
	const { data: marketListData } = useGetMarketListQuery();
	const { data: metarialFabricListData } = useGetMetarialFabricListQuery({
		search: metarialFabricSearch ? { search: metarialFabricSearch } : {},
		page: 0,
		size: 20
	});
	const [materialSearchOrAdd, { isLoading: isMaterialLoading }] = useMaterialSearchOrAddMutation();
	const [setProductStyleInfo, { isLoading: isProductStyleInfoLoading }] = useSetProductStyleInfoMutation();
	const [setProductInfo, { isLoading: isProductInfoLoading }] = useSetProductInfoMutation();

	useEffect(() => {
		if (washListData) {
			setWashList(washListData);
		}
		if (printListData) {
			setPrintList(printListData);
		}
		if (embroideryListData) {
			setEmbroideryList(embroideryListData);
		}
		if (categorySubCategoryListData) {
			setCategorySubCategoryList(categorySubCategoryListData);
		}
		if (marketListData) {
			setMarketList(marketListData);
		}
		if (metarialFabricListData) {
			setMetarialFabricList(normalizeList(metarialFabricListData));
		}
	}, [
		washListData,
		printListData,
		embroideryListData,
		categorySubCategoryListData,
		marketListData,
		metarialFabricListData
	]);

	const matchedProductIds = productIds || [];
	const byProductId = documentExtractionSocket?.byProductId ?? EMPTY_BY_PRODUCT_ID;

	const isMissingValue = (value) => !extractionText(value);

	const isBlankMissing = (value) => !extractionText(value);

	const labelOrValue = (value, key) => {
		const t = extractionText(value);
		if (!t) return key;
		return t;
	};

	const extractionPendingByProductId = new Map(
		(extractionPendingList || [])
			.filter((item) => item?.productId !== undefined && item?.productId !== null)
			.map((item) => [
				item.productId,
				{ name: item?.name, docUrl: item?.docUrl ?? item?.documentResponse?.docUrl }
			])
	);
	//check extraction status missing or running
	const getExtractionStatus = (socketMsg) => {
		if (!socketMsg || !socketMsg.data) return 'RUNNING';

		const extractionData = socketMsg.data.extractionData;
		// Document processed but no structured extraction — allow manual fill (same as empty object)
		if (extractionData === null) return 'MISSING';
		if (!extractionData || typeof extractionData !== 'object') return 'RUNNING';

		const bodyFabric = extractionData['bodyFabric'];
		const gsmFromBody =
			bodyFabric && typeof bodyFabric === 'object' && bodyFabric.gsm != null
				? String(bodyFabric.gsm).trim()
				: '';
		const constructionFromBody = bodyFabric?.construction;
		const constructionType = constructionFromBody?.name;

		const compositionFromBody = formatCompositionFromBodyFabric(bodyFabric);

		const buyerDesignNo = extractionData['buyerDesignNo'] ?? extractionData['buyer_design_no'];
		const marketField = extractionData.market ?? extractionData['Market'];
		const categoryField = extractionData.category ?? extractionData['Category'];
		const fabricTypeField = constructionType;
		const gsm =
			extractionData['GSM/weight'] ?? extractionData['GSM'] ?? extractionData['gsm'] ?? gsmFromBody;
		const composition =
			extractionData['Composition (%)'] ?? extractionData['Composition'] ?? compositionFromBody;
		const print = extractionData['print'];
		const embroidery = extractionData['embroidery'];
		const wash = extractionData['wash'];

		const allRequiredFilled =
			!isMissingValue(buyerDesignNo) &&
			!isMissingValue(marketField) &&
			!isMissingValue(categoryField) &&
			!isMissingValue(fabricTypeField) &&
			!isMissingValue(gsm) &&
			!isMissingValue(composition) &&
			!isMissingValue(print) &&
			!isMissingValue(embroidery) &&
			!isMissingValue(wash);

		return allRequiredFilled ? 'COMPLETED' : 'MISSING';
	};

	const getByProductIdMsg = (productId) => {
		if (!productId && productId !== 0) return undefined;
		return byProductId[productId];
	};

	const sortedMatchedProductIds = useMemo(() => {
		const ids = matchedProductIds;
		if (!ids.length) return [];
		return [...ids].sort((a, b) =>
			String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
		);
	}, [matchedProductIds]);

	const visibleMatchedProductIds = useMemo(
		() => sortedMatchedProductIds.filter((id) => !removedByProductId[id]),
		[sortedMatchedProductIds, removedByProductId]
	);

	useEffect(() => {
		MergedFilesCount?.(visibleMatchedProductIds.length);
	}, [visibleMatchedProductIds.length, MergedFilesCount]);

	useEffect(() => {
		return () => {
			if (markCompleteRemoveTimeRef.current != null) {
				clearTimeout(markCompleteRemoveTimeRef.current);
			}
		};
	}, []);

	//get loading state for document extraction socket
	const isDocumentExtractionSocketLoading =
		visibleMatchedProductIds.length > 0 &&
		visibleMatchedProductIds.every((productId) => {
			const socketMsg = getByProductIdMsg(productId);
			return getExtractionStatus(socketMsg) === 'RUNNING';
		});

	const selectedFabricationFields = useMemo(() => {
		if (selectedProductId == null) return null;
		const staged = pendingUpdateByProductId[selectedProductId];
		const socketEd = getByProductIdMsg(selectedProductId)?.data?.extractionData || {};
		const ed = staged || socketEd;
		const pending = pendingFabricByProductId[selectedProductId];
		const merged = pending ? applyFabricModalPayloadToExtraction({ ...ed }, pending) : ed;
		return getFabricationRawFields(merged);
	}, [selectedProductId, byProductId, pendingFabricByProductId, pendingUpdateByProductId]);

	const modalInitialComposition = useMemo(() => {
		if (selectedProductId == null) return undefined;
		const pending = pendingFabricByProductId[selectedProductId];
		if (pending?.composition?.length) return pending.composition;
		const staged = pendingUpdateByProductId[selectedProductId];
		const socketEd = getByProductIdMsg(selectedProductId)?.data?.extractionData || {};
		const ed = staged || socketEd;
		const fromBody = ed?.bodyFabric?.composition;
		if (Array.isArray(fromBody) && fromBody.length) return fromBody;
		const parsed = compositionRowsFromText(getFabricationRawFields(ed).compositionValue);
		return parsed.length ? parsed : undefined;
	}, [selectedProductId, byProductId, pendingFabricByProductId, pendingUpdateByProductId]);

	/** Material fabrication list id ≠ construction master id; avoid wrong modal prefill when dropdown has a list item. */
	const skipModalPrefillFromMaterialFabricDropdown = useMemo(() => {
		const rawId = metarialFabricSelection?.id;
		if (rawId == null || rawId === '') return false;
		const n = Number(rawId);
		return Number.isFinite(n) && n > 0;
	}, [metarialFabricSelection]);

	// Basic + embellishment fields only. Fabric is synced in a separate effect so opening the fabric
	// modal / material list refetch does not reset print, wash, etc. from stale extractionData.
	useEffect(() => {
		if (selectedProductId == null) {
			setBuyerDesignNoInput('');
			setMarketSelection(null);
			setCategorySelection(null);
			setPrintSelection(null);
			setEmbroiderySelection(null);
			setWashSelection(null);
			setMetarialFabricSelection(null);
			setSelectedFabricInfoValue('');
			setSelectedDocUrl('');
			setPreviewDocName('');
			return;
		}

		const socketMsg = byProductId[selectedProductId];
		const stagedExtractionData = pendingUpdateByProductId[selectedProductId];
		const ed = stagedExtractionData || socketMsg?.data?.extractionData || {};
		setBuyerDesignNoInput(extractionText(ed.buyerDesignNo ?? ed.buyer_design_no));
		setMarketSelection(findListMatch(marketList, ed.market ?? ed.Market, 'id', 'name'));
		const categoryFlatList = categorySubCategoryList.flatMap((item) =>
			(item?.subCategoryResponseList || []).map((subCategory) => ({
				id: subCategory?.id,
				name: subCategory?.name
			}))
		);
		const rawCategory = extractionText(ed.category ?? ed['Category']);
		const normalizedRawCategory = rawCategory.toLowerCase();
		const matchedCategory = rawCategory
			? categoryFlatList.find((item) => {
					if (String(item?.id ?? '') === String(rawCategory)) return true;
					return extractionText(item?.name).toLowerCase() === normalizedRawCategory;
				})
			: null;
		setCategorySelection(
			matchedCategory
				? { id: matchedCategory.id, value: matchedCategory.name }
				: rawCategory
					? { id: null, value: rawCategory }
					: null
		);
		setPrintSelection(findListMatch(printList, ed.print, 'id', 'name'));
		setEmbroiderySelection(findListMatch(embroideryList, ed.embroidery, 'id', 'name'));
		setWashSelection(findListMatch(washList, ed.wash, 'id', 'name'));
	}, [
		selectedProductId,
		byProductId,
		marketList,
		categorySubCategoryList,
		printList,
		embroideryList,
		washList,
		pendingUpdateByProductId
	]);
	useEffect(() => {
		if (selectedProductId == null) return;

		const socketMsg = byProductId[selectedProductId];
		const stagedExtractionData = pendingUpdateByProductId[selectedProductId];
		const ed = stagedExtractionData || socketMsg?.data?.extractionData || {};
		const fabricDraft = pendingFabricByProductId[selectedProductId];
		const edFabric = fabricDraft ? applyFabricModalPayloadToExtraction({ ...ed }, fabricDraft) : ed;
		const rawFabric = edFabric['Fabric type'] ?? edFabric.fabricType;
		setSelectedFabricInfoValue(buildSelectedFabricInfoValue(edFabric));
		setMetarialFabricSelection(findListMatch(metarialFabricList, rawFabric, 'id', 'name'));
	}, [
		selectedProductId,
		byProductId,
		metarialFabricList,
		pendingFabricByProductId,
		pendingUpdateByProductId
	]);

	const handleSaveTpInfo = () => {
		if (selectedProductId == null) return;
		const prevExtra = getByProductIdMsg(selectedProductId)?.data?.extractionData || {};
		const stagedEd = pendingUpdateByProductId[selectedProductId];
		const draft = pendingFabricByProductId[selectedProductId];
		const mergedForFabric = draft
			? applyFabricModalPayloadToExtraction({ ...(stagedEd || prevExtra) }, draft)
			: stagedEd || prevExtra;
		const extractionFabric = mergedForFabric['Fabric type'] ?? mergedForFabric.fabricType;
		let fabricSelection = metarialFabricSelection;
		for (const raw of [selectedFabricInfoValue, extractionFabric]) {
			if (fabricSelection) break;
			fabricSelection = findListMatch(metarialFabricList, raw, 'id', 'name');
		}
		if (!fabricSelection) {
			const line = extractionText(selectedFabricInfoValue) || extractionText(extractionFabric);
			if (line) fabricSelection = { value: line };
		}
		let nextExtraction = applyFabricDropdownToExtraction(
			{
				...prevExtra,
				buyerDesignNo: buyerDesignNoInput.trim(),
				buyer_design_no: buyerDesignNoInput.trim(),
				market: marketSelection?.value ?? '',
				category: categorySelection?.value ?? '',
				print: printSelection?.value ?? '',
				embroidery: embroiderySelection?.value ?? '',
				wash: washSelection?.value ?? ''
			},
			fabricSelection
		);
		if (draft) {
			nextExtraction = applyFabricModalPayloadToExtraction(nextExtraction, draft);
		}
		setPendingUpdateByProductId((prev) => ({
			...prev,
			[selectedProductId]: nextExtraction
		}));
		if (draft) {
			setPendingFabricByProductId((prev) => {
				const next = { ...prev };
				delete next[selectedProductId];
				return next;
			});
		}
		setShowMarkCompleteAfterUpdateByProductId((prev) => ({
			...prev,
			[selectedProductId]: true
		}));
		toastSuccess('Draft saved. Click "Mark as complete" to apply update.');
		setIsUpdateFormToggle(false);
	};

	const handleFabricModalSubmit = (payload) => {
		if (selectedProductId == null) return;
		const previousSocketMessage = getByProductIdMsg(selectedProductId);

		setPendingFabricByProductId((prev) => ({ ...prev, [selectedProductId]: payload }));
		const ed =
			pendingUpdateByProductId[selectedProductId] || previousSocketMessage?.data?.extractionData || {};
		const merged = applyFabricModalPayloadToExtraction({ ...ed }, payload);
		const mergedFabricationFields = getFabricationRawFields(merged);
		const fallbackLine = [payload?.fabricTypeLabel || payload?.fabricTypeCode, payload?.weight]
			.map((v) => extractionText(v))
			.filter(Boolean)
			.join(', ');
		setSelectedFabricInfoValue(buildSelectedFabricInfoValue(merged) || fallbackLine);
		const matchedFabricSelection =
			findListMatch(
				metarialFabricList,
				mergedFabricationFields?.constructionId || payload?.fabricTypeCode,
				'id',
				'name'
			) ||
			findListMatch(
				metarialFabricList,
				mergedFabricationFields?.constructionValue ||
					payload?.fabricTypeLabel ||
					merged.fabricType ||
					merged['Fabric type'],
				'id',
				'name'
			);
		setMetarialFabricSelection(matchedFabricSelection);
		toastSuccess('Fabric details saved. Click Update below to apply to the list.');
		setIsShowUpdateMissingFabricInfoModal(false);
	};

	const applyMissingRowSelection = (productId, displayFileName) => {
		const rowSocket = getByProductIdMsg(productId);
		const rowExtractionData = rowSocket?.data?.extractionData || {};
		const fabricDraft = pendingFabricByProductId[productId];
		const merged = fabricDraft
			? applyFabricModalPayloadToExtraction({ ...rowExtractionData }, fabricDraft)
			: rowExtractionData;
		setSelectedFabricInfoValue(buildSelectedFabricInfoValue(merged));
		setSelectedProductId(productId);
		const pending = extractionPendingByProductId.get(productId);
		const url = (pending?.docUrl || rowSocket?.data?.documentResponse?.docUrl || '').trim();
		setSelectedDocUrl(url);
		setPreviewDocName(displayFileName || '');
		setIsUpdateFormToggle(true);
	};

	const handleTechpackListItemClick = (productId, fileName) => {
		if (selectedProductId === productId) {
			setIsUpdateFormToggle((v) => !v);
			return;
		}
		applyMissingRowSelection(productId, fileName);
	};

	const handleMarkAsComplete = async (productId, e) => {
		e?.stopPropagation();
		const stagedExtractionData = pendingUpdateByProductId[productId];
		const extractionData =
			stagedExtractionData || getByProductIdMsg(productId)?.data?.extractionData || {};
		const fabricDraft = pendingFabricByProductId[productId];
		const merged = fabricDraft
			? applyFabricModalPayloadToExtraction({ ...extractionData }, fabricDraft)
			: extractionData;
		const body = buildMaterialSearchOrAddBody(merged);
		const printSelForRow = findListMatch(printList, merged.print, 'id', 'name');
		const embroiderySelForRow = findListMatch(embroideryList, merged.embroidery, 'id', 'name');
		const washSelForRow = findListMatch(washList, merged.wash, 'id', 'name');
		const extractionFabric = merged['Fabric type'] ?? merged.fabricType;
		const fabricInfoLine = buildSelectedFabricInfoValue(merged);
		let fabricSelForRow = null;
		for (const raw of [fabricInfoLine, extractionFabric]) {
			fabricSelForRow = findListMatch(metarialFabricList, raw, 'id', 'name');
			if (fabricSelForRow) break;
		}
		if (!fabricSelForRow) {
			const line = extractionText(fabricInfoLine) || extractionText(extractionFabric);
			if (line) fabricSelForRow = { value: line };
		}
		try {
			const selectedFabricationItem = Number(fabricSelForRow?.id);
			const hasValidSelectedFabricId =
				Number.isFinite(selectedFabricationItem) && selectedFabricationItem > 0;
			const response = hasValidSelectedFabricId
				? selectedFabricationItem
				: await materialSearchOrAdd(body).unwrap();

			await setProductStyleInfResponse(response, productId, merged);
			await setProductInfo({
				id: productId,
				requestBody: {
					printCategoryIdList: printSelForRow ? [printSelForRow.id] : [],
					washCategoryIdList: washSelForRow ? [washSelForRow.id] : [],
					embroideryCategoryIdList: embroiderySelForRow ? [embroiderySelForRow.id] : []
				}
			});
			const prevMsg = getByProductIdMsg(productId);
			const prevData = prevMsg?.data || {};
			dispatch(
				setDocumentExtractionSocketMessage({
					...prevMsg,
					data: {
						...prevData,
						productId,
						extractionData: merged
					}
				})
			);

			if (selectedProductId === productId) {
				setSelectedProductId(null);
			}
			setPendingUpdateByProductId((prev) => {
				const next = { ...prev };
				delete next[productId];
				return next;
			});
			setPendingFabricByProductId((prev) => {
				if (!prev[productId]) return prev;
				const next = { ...prev };
				delete next[productId];
				return next;
			});
			setShowMarkCompleteAfterUpdateByProductId((prev) => ({
				...prev,
				[productId]: false
			}));
			toastSuccess(response?.message || 'Marked as complete successfully');

			setIsMarkedAsAnimate(productId);
			if (markCompleteRemoveTimeRef.current != null) {
				clearTimeout(markCompleteRemoveTimeRef.current);
			}
			markCompleteRemoveTimeRef.current = window.setTimeout(() => {
				markCompleteRemoveTimeRef.current = null;
				setRemovedByProductId((prev) => ({ ...prev, [productId]: true }));
				setIsMarkedAsAnimate((current) => (current === productId ? null : current));
			}, 500);
		} catch (error) {
			toastError('Fabrication Data not found' || error?.data?.message || error?.message);
		}
	};

	const setProductStyleInfResponse = async (responseId, productId, mergedExtraction) => {
		const ed = mergedExtraction;
		const fabricationFields = getFabricationRawFields(ed);
		const ref = extractionText(ed.buyerDesignNo ?? ed.buyer_design_no);
		const marketSel = findListMatch(marketList, ed.market ?? ed.Market, 'id', 'name');
		const categoryFlat = categorySubCategoryList.flatMap((item) =>
			(item?.subCategoryResponseList || []).map((subCategory) => ({
				id: subCategory?.id,
				name: subCategory?.name
			}))
		);
		const categorySel = findListMatch(categoryFlat, ed.category ?? ed['Category'], 'id', 'name');
		try {
			await setProductStyleInfo({
				id: productId,
				requestBody: {
					id: productId,
					referenceNumber: ref,
					fabricId: responseId,
					productGroupId: marketSel?.id,
					productSubCategoryId: categorySel?.id,
					isNitexProduct: false,
					fabricType: fabricationFields?.constructionValue
				}
			}).unwrap();
		} catch (error) {
			toastError(error?.data?.message || error?.message || 'Product style info failed');
		}
	};

	const onGetBodyFabrics = (searchValue = '') => {
		setMetarialFabricSearch(searchValue.trim());
	};

	const updateTechpackInfoFormProps = {
		selectedProductId,
		buyerDesignNoInput,
		onBuyerDesignNoChange: setBuyerDesignNoInput,
		marketList,
		marketSelection,
		onMarketSelection: setMarketSelection,
		categorySubCategoryList,
		categorySelection,
		onCategorySelection: setCategorySelection,
		printList,
		printSelection,
		onPrintSelection: setPrintSelection,
		embroideryList,
		embroiderySelection,
		onEmbroiderySelection: setEmbroiderySelection,
		washList,
		washSelection,
		onWashSelection: setWashSelection,
		metarialFabricList,
		metarialFabricSelection,
		onMetarialFabricSelection: setMetarialFabricSelection,
		onGetBodyFabrics,
		selectedFabricInfoValue,
		selectedFabricationFields,
		onOpenMissingFabricModal: () => setIsShowUpdateMissingFabricInfoModal(true),
		setIsUpdateFormToggle,
		onSave: handleSaveTpInfo
	};

	const renderTechpackList = () => {
		return (
			<>
				{visibleMatchedProductIds.flatMap((productId) => {
					const socketMsg = getByProductIdMsg(productId);
					const extractionStatus = getExtractionStatus(socketMsg);
					const isRowSelectable = extractionStatus !== 'RUNNING';
					const cardClassName = [
						extractionStatus === 'MISSING'
							? 'teckpack-list-item missing-document-extraction mb-12 cursor-pointer bg-white'
							: 'teckpack-list-item mb-12 bg-white',
						isRowSelectable ? 'cursor-pointer' : '',
						selectedProductId === productId ? 'is-selected' : '',
						isMarkedAsAnimate == productId ? 'animate-mark-as-complete' : ''
					]
						.filter(Boolean)
						.join(' ');
					const stagedExtractionData = pendingUpdateByProductId[productId];
					const extractionData = stagedExtractionData || socketMsg?.data?.extractionData || {};

					// values from extractionData (keys vary: buyer_design_no, print, Fabric type, …)
					const { compositionValue, gsmValue, constructionValue, constructionId } =
						getFabricationRawFields(extractionData);

					const categoryValue = extractionData.category ?? extractionData['Category'];
					const printValue = extractionData['print'];
					const embroideryValue = extractionData['embroidery'];
					const washValue = extractionData['wash'];
					const marketValue = extractionData['market'] ?? extractionData['Market'];
					const buyerDesignNoValue =
						extractionData['buyerDesignNo'] || extractionData['buyer_design_no'];

					const buyerDesignNo = labelOrValue(buyerDesignNoValue, TP_ROW_EMPTY_LABEL.buyerDesignNo);
					const market = labelOrValue(marketValue, TP_ROW_EMPTY_LABEL.market);
					const category = labelOrValue(categoryValue, TP_ROW_EMPTY_LABEL.category);
					const print = labelOrValue(printValue, TP_ROW_EMPTY_LABEL.print);
					const embroidery = labelOrValue(embroideryValue, TP_ROW_EMPTY_LABEL.embroidery);
					const wash = labelOrValue(washValue, TP_ROW_EMPTY_LABEL.wash);
					const constructionTypeValue = labelOrValue(
						constructionValue,
						TP_ROW_EMPTY_LABEL.fabricType
					);
					const composition = labelOrValue(compositionValue, TP_ROW_EMPTY_LABEL.composition);
					const gsm = labelOrValue(gsmValue, TP_ROW_EMPTY_LABEL.gsm);

					const missingFlags = {
						buyerDesignNo: isBlankMissing(buyerDesignNoValue),
						market: isBlankMissing(marketValue),
						category: isBlankMissing(categoryValue),
						print: isBlankMissing(printValue),
						embroidery: isBlankMissing(embroideryValue),
						wash: isBlankMissing(washValue),
						composition: isBlankMissing(compositionValue),
						gsm: isBlankMissing(gsmValue),
						constructionType: isBlankMissing(constructionValue)
					};
					const showMarkCompleteBtn = showMarkCompleteAfterUpdateByProductId[productId];

					const pendingEntry = extractionPendingByProductId.get(productId);
					let fileName = pendingEntry?.name || '';
					if (socketMsg && socketMsg.data) {
						if (socketMsg.data.documentResponse && socketMsg.data.documentResponse.name) {
							fileName = socketMsg.data.documentResponse.name;
						} else if (socketMsg.data.fileName) {
							fileName = socketMsg.data.fileName;
						} else if (socketMsg.data.documentName) {
							fileName = socketMsg.data.documentName;
						}
					}

					const listItemEl = (
						<div
							key={productId}
							className={cardClassName}
							data-construction-id={
								constructionId != null && constructionId !== ''
									? String(constructionId)
									: undefined
							}
							onClick={() => {
								if (!isRowSelectable) return;
								handleTechpackListItemClick(productId, fileName);
							}}
							onKeyDown={(e) => {
								if (!isRowSelectable) return;
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									handleTechpackListItemClick(productId, fileName);
								}
							}}
							role={isRowSelectable ? 'button' : undefined}
							tabIndex={isRowSelectable ? 0 : undefined}
						>
							{extractionStatus === 'MISSING' || extractionStatus === 'COMPLETED' ? (
								<div className="icon">
									<SuccessIcon width={20} height={20} />
								</div>
							) : (
								<>
									<div className="icon">
										<CommonImage
											src={`${process.env.PUBLIC_URL}/icons/file.svg`}
											alt=""
										/>
									</div>
								</>
							)}

							<div className="left-info">
								<ul className="common-list-component mb-2">
									<li
										className={`medium-14 ${
											extractionStatus === 'MISSING' && missingFlags.buyerDesignNo
												? 'red'
												: 'gray_dark_01'
										}`}
									>
										{buyerDesignNo}
									</li>
									<li
										className={`medium-14 ${
											extractionStatus === 'MISSING' && missingFlags.market
												? 'red'
												: 'gray_dark_01'
										}`}
									>
										{market}
									</li>
									<li
										className={`medium-14 ${
											extractionStatus === 'MISSING' && missingFlags.category
												? 'red'
												: 'gray_dark_01'
										}`}
									>
										{category}
									</li>
								</ul>

								<ul className="common-list-component mb-1">
									<li
										className={`regular-14 ${
											extractionStatus === 'MISSING' && missingFlags.print
												? 'red'
												: 'gray_dark_01'
										}`}
									>
										{print}
									</li>
									<li
										className={`regular-14 ${
											extractionStatus === 'MISSING' && missingFlags.embroidery
												? 'red'
												: 'gray_dark_01'
										}`}
									>
										{embroidery}
									</li>
									<li
										className={`regular-14 ${
											extractionStatus === 'MISSING' && missingFlags.wash
												? 'red'
												: 'gray_dark_01'
										}`}
									>
										{wash}
									</li>
								</ul>

								<ul className="common-list-component mb-1">
									<li
										className={`regular-14 ${extractionStatus === 'MISSING' && missingFlags.constructionType ? 'red' : 'gray_dark_01'}`}
									>
										{constructionTypeValue}
									</li>
									<li
										className={`regular-14 ${
											extractionStatus === 'MISSING' && missingFlags.composition
												? 'red'
												: 'gray_dark_01'
										}`}
									>
										{composition}
									</li>
									<li
										className={`regular-14 ${
											extractionStatus === 'MISSING' && missingFlags.gsm
												? 'red'
												: 'gray_dark_01'
										}`}
									>
										{gsm}
									</li>
								</ul>
								<Tooltip title={fileName} placement="top" arrow>
									<p className="file-name regular-12 gray_dark_01 mt-2">
										{getShortName(fileName, 40)}
									</p>
								</Tooltip>
							</div>

							<div className="right-info d-flex flex-row justify-content-center align-items-center">
								{extractionStatus === 'RUNNING' ? (
									<div className="d-flex flex-row justify-content-center align-items-start gap-2">
										<LoadIcon className="uploading-icon" width={20} height={20} />
										<p className="regular-12 blue mb-0">Data extraction running</p>
									</div>
								) : (
									<div className="d-flex flex-row justify-content-center align-items-start gap-2">
										{showMarkCompleteBtn || extractionStatus === 'COMPLETED' ? (
											<button
												type="button"
												className="button primary size32 mark-as-complete-btn"
												onClick={(e) => handleMarkAsComplete(productId, e)}
												disabled={
													isMaterialLoading ||
													isProductStyleInfoLoading ||
													isProductInfoLoading ||
													isMarkedAsAnimate == productId
												}
											>
												Mark as complete
											</button>
										) : (
											<>
												<MissingIcon width={20} height={20} />
												<p className="regular-12 red mb-0">Missing info</p>
											</>
										)}
									</div>
								)}
							</div>
						</div>
					);

					return selectedProductId === productId
						? [
								listItemEl,
								isUpdateFormToggle ? (
									<UpdateTechpackInfoForm
										key={`${productId}-update-tp-info`}
										{...updateTechpackInfoFormProps}
									/>
								) : null
							]
						: [listItemEl];
				})}
			</>
		);
	};

	return (
		<LoaderComponent loading={isDocumentExtractionSocketLoading}>
			{visibleMatchedProductIds.length > 0 ? (
				<div className="multiple-techpack-preview-page pt-3 px-4 h-100 d-flex justify-content-between align-items-start">
					<div className="teckpack-sidebar-and-update-form">
						<div className="teckpack-list scroll-y-label">
							<p className="upload-count regular-14 gray_dark_01 mb-2">
								Uploaded files: {visibleMatchedProductIds.length}
							</p>
							{renderTechpackList()}
						</div>
					</div>
					<Preview
						docUrl={selectedDocUrl}
						docName={previewDocName}
						selectedProductId={selectedProductId}
						isLoading={isMaterialLoading || isProductStyleInfoLoading || isProductInfoLoading}
					/>
					{isShowUpdateMissingFabricInfoModal && (
						<AddFabricCompositionModal
							productId={selectedProductId}
							selectedFabricType={
								skipModalPrefillFromMaterialFabricDropdown
									? ''
									: extractionText(selectedFabricationFields?.constructionValue)
							}
							selectedConstructionId={
								skipModalPrefillFromMaterialFabricDropdown
									? undefined
									: selectedFabricationFields?.constructionId
							}
							selectedCompositionName={
								skipModalPrefillFromMaterialFabricDropdown
									? undefined
									: selectedFabricationFields?.compositionValue
							}
							selectedGsmValue={extractionText(selectedFabricationFields?.gsmValue)}
							initialComposition={
								skipModalPrefillFromMaterialFabricDropdown
									? undefined
									: modalInitialComposition
							}
							onSubmit={handleFabricModalSubmit}
							onClose={() => setIsShowUpdateMissingFabricInfoModal(false)}
						/>
					)}
				</div>
			) : (
				<div className="d-flex justify-content-center align-items-center h-100">
					<p>No product selected</p>
				</div>
			)}
		</LoaderComponent>
	);
};

export default MultipleTechpackPreview;
